import {defaultHandlerCatch, defaultHandlerResolver, promisifyImage} from "./helpers";
import {photolabTask} from "../api";
import {extraKeys} from "../etc";
import {PhotolabTaskBuilder} from "@vicman-wi/photolab-api-js";

const tasksCache = {};

function photolabTaskHelper(creative, taskName, taskConfig, options) {
  const taskCacheKey = JSON.stringify(taskConfig);
  const _options = Object.assign({
    timeout: 1000,
    interval: 1000,
  }, options);

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: _options.timeout,
      interval: _options.interval,
    })
  }

  return tasksCache[taskCacheKey].then((taskResult) => {
    creative.setTask(taskName, taskResult);
    return taskResult;
  }).catch((err) => {
    delete tasksCache[taskCacheKey];
    throw err;
  });
}

async function promptTask(processing, creative, userFile) {
  const promptValues = {
    gender: processing.getExtra(extraKeys.gender),
    age_group: processing.getExtra(extraKeys.ageGroup),
    race: processing.getExtra(extraKeys.race),
    skin_color: processing.getExtra(extraKeys.skinColor),
    hair_length: processing.getExtra(extraKeys.hairStyle2),
    hair_color: processing.getExtra(extraKeys.hairColor2),
  };

  return await photolabTaskHelper(creative, "prompt", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addCollageMethod({
      "@transformationId": "npp_base",
      "@configId": creative.getExtra("config_id"),
      "@values": [
        {
          parameters: ["prompt"],
          values: promptValues,
        },
      ],
      param_sds: creative.getExtra(extraKeys.seed),
      gender: processing.getExtra(extraKeys.gender),
    })
    .addImage(userFile.url)
    .setLanguage(processing.language)
    .build());
}

async function roopTask(processing, creative, userFileUrl, promptResultFileUrl) {
  let targetFaceIndex = 0;
  const getFacesTaskResult = await photolabTaskHelper(creative, "roop_get_faces", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", mode: "get_faces"})
    .build());

  if (getFacesTaskResult.humans?.items?.length > 1) {
    targetFaceIndex = getFacesTaskResult.humans.items
      .reduce((v, {bbox}, index) => {
        const square = (bbox.maxPoint.x - bbox.minPoint.x) * (bbox.maxPoint.y - bbox.minPoint.y);
        if (v.square < square) {
          return {index, square};
        } else {
          return v;
        }
      }, {index: 0, square: 0})
      .index;
  }

  return await photolabTaskHelper(creative, "roop", new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addImage(userFileUrl)
    .addImage(promptResultFileUrl)
    .addCollageMethod({template_name: "nn_roop", target_face_index: targetFaceIndex})
    .build());
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */

export default (processing, creative, options) => {
  return new Promise(async (resolve, reject) => {
    try {
      options = Object.assign({}, options);

      const userFile = processing.file;

      const promptTaskResult = await promptTask(processing, creative, userFile);

      let finalTaskResult = promptTaskResult;

      try {
        finalTaskResult = await roopTask(processing, creative, userFile, promptTaskResult.resultUrl);
      } catch(err) {
        console.err(err);
      }

      await promisifyImage(finalTaskResult.resultUrl);

      creative.setFile("raw", finalTaskResult.resultUrl);
      creative.markAsProcessed(finalTaskResult.resultUrl);

      defaultHandlerResolver(creative, resolve)();
    } catch (err) {
      defaultHandlerCatch(creative, reject)(err);
    }
  });
}